import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CurrencyGbp,
  Tag,
  Package,
  ShoppingBagOpen,
  Sparkle,
  Camera,
  Hoodie,
  TrendUp,
  Star,
} from "@phosphor-icons/react";
import { listItems, getStats, formatPrice, imageSrc } from "../lib/api";

const StatCard = ({ label, value, sub, bg = "bg-white", span = "", icon, testid }) => (
  <div data-testid={testid} className={`nb-card nb-card-hover ${bg} ${span}`}>
    <div className="flex items-start justify-between">
      <span className="nb-label">{label}</span>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">{icon}</div>
    </div>
    <div className="mt-6 font-display text-4xl font-black leading-none tracking-tighter sm:text-5xl">{value}</div>
    {sub && <div className="mt-3 text-sm font-medium text-[#4A4A4A]">{sub}</div>}
  </div>
);

const statusStyles = { draft: "bg-[#EBE8E0]", listed: "bg-[#B4F0A7]", sold_out: "bg-[#FFDF85]" };
const statusLabel = (s) => (s === "sold_out" ? "sold out" : s);

const ItemCard = ({ item }) => {
  const stock = Math.max(0, (item.quantity || 1) - (item.quantity_sold || 0));
  return (
    <Link to={`/items/${item.id}`} data-testid={`item-card-${item.id}`} className="nb-card nb-card-hover block bg-white">
      <div className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded-lg border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
        {imageSrc(item) ? <img src={imageSrc(item)} alt={item.product_name} className="h-full w-full object-cover" /> : <Hoodie weight="duotone" size={56} />}
        <span className="absolute right-3 top-3 nb-badge bg-white">{stock}/{item.quantity} left</span>
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className={`nb-badge ${statusStyles[item.status] || "bg-white"}`}>{statusLabel(item.status)}</span>
        <span className="font-display text-lg font-black">{formatPrice(item.listing_price || item.estimated_value_max)}</span>
      </div>
      <h3 className="mt-3 line-clamp-2 font-display text-lg font-black leading-tight">{item.suggested_title || item.product_name}</h3>
      <div className="mt-1 text-xs font-medium uppercase tracking-wider text-[#4A4A4A]">
        {item.brand || "Unbranded"} · {item.category || "—"}
      </div>
    </Link>
  );
};

const Empty = () => (
  <div data-testid="empty-inventory" className="nb-card flex flex-col items-center justify-center border-dashed bg-[#FDFBF7] py-16 text-center" style={{ borderStyle: "dashed" }}>
    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-[3px] border-[#0A0A0A] bg-[#D4C4FB] shadow-[4px_4px_0px_0px_#0A0A0A]">
      <Camera weight="duotone" size={40} />
    </div>
    <h3 className="mt-6 font-display text-3xl font-black tracking-tight">Snap your first flip</h3>
    <p className="mt-2 max-w-md text-base font-medium text-[#4A4A4A]">Upload a photo and we'll identify the item, estimate resale value, and write the perfect listing.</p>
    <Link to="/scan" data-testid="empty-scan-cta" className="nb-btn mt-6 inline-flex items-center gap-2">
      <Camera weight="bold" size={18} /> Scan an item
    </Link>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([getStats(), listItems()]);
      setStats(s);
      setItems(i);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <span className="nb-label">Seller HQ</span>
          <h1 className="mt-2 font-display text-4xl font-black leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            Flip smarter,<br />not harder.
          </h1>
          {stats?.seller_rating?.count > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
              <Star weight="fill" size={16} /> {stats.seller_rating.average.toFixed(1)} from {stats.seller_rating.count} review{stats.seller_rating.count !== 1 ? "s" : ""}
            </div>
          )}
        </div>
        <Link to="/scan" data-testid="scan-cta" className="nb-btn inline-flex items-center gap-2">
          <Camera weight="bold" size={18} /> New scan
        </Link>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
        <StatCard testid="stat-profit" label="Total profit" value={formatPrice(stats?.total_profit || 0)} sub={`${stats?.units_sold || 0} units sold · avg ${formatPrice(stats?.avg_profit || 0)}`} bg="bg-[#B4F0A7]" span="md:col-span-2 md:row-span-2" icon={<CurrencyGbp weight="duotone" size={20} />} />
        <StatCard testid="stat-inventory" label="In stock" value={stats?.units_in_stock || 0} sub={`${stats?.listed || 0} listed · ${stats?.drafts || 0} drafts`} bg="bg-[#D4C4FB]" icon={<Package weight="duotone" size={20} />} />
        <StatCard testid="stat-potential" label="Potential value" value={formatPrice(stats?.potential_value_high || 0)} sub={`Low end ${formatPrice(stats?.potential_value_low || 0)}`} bg="bg-[#FFDF85]" icon={<TrendUp weight="duotone" size={20} />} />
        <StatCard testid="stat-revenue" label="Revenue" value={formatPrice(stats?.total_revenue || 0)} sub={`Shipping collected ${formatPrice(stats?.total_shipping_collected || 0)}`} bg="bg-white" icon={<ShoppingBagOpen weight="duotone" size={20} />} />
        <StatCard testid="stat-totalitems" label="Listings" value={stats?.total_items || 0} sub={`${stats?.sold_out || 0} sold out`} bg="bg-white" icon={<Tag weight="duotone" size={20} />} />
      </div>

      <div className="mt-14 flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="nb-label">Inventory</span>
          <h2 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-4xl">Your closet, your cash.</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {[["all", "all"], ["draft", "drafts"], ["listed", "listed"], ["sold_out", "sold out"]].map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`nb-badge cursor-pointer ${filter === k ? "bg-[#D4C4FB]" : "bg-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="nb-card flex items-center gap-3 bg-white">
            <Sparkle weight="duotone" size={22} className="animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <Empty />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((it) => <ItemCard key={it.id} item={it} />)}
          </div>
        )}
      </div>
    </div>
  );
}
